/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { lazy, Suspense, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { LoadingPage, ModuleHeader, RequiredIndicator } from '@components';
import { useYupSchema } from '@hooks';
import { useStore } from '@stores';
import { globalTheme as theme } from '@styles';
import {
  DESKTOP_SCROLLING_DIV_ID,
  FOOTER_DIV_ID,
  MOBILE_SCROLLING_DIV_ID,
  shouldShowIntroUI,
  sendEventToHost,
  retryImport,
} from '@utils';

import { startAppCheck } from '../../../firebase.config';
import { ModalFooter } from '../FormModal/ModalFooter';

// Terminal screens load on demand: they (and framer-motion, lottie, dompurify, the
// icon sets behind them) are not needed for the first step. Leaf-level lazy imports
// leave vendor initialization order untouched; a failed chunk is retried once.
const ModuleIntro = lazy(() =>
  retryImport(() => import('../../misc/ModuleIntro')).then((m) => ({
    default: m.ModuleIntro,
  }))
);
const ModuleReview = lazy(() =>
  retryImport(() => import('../../misc/ModuleReview')).then((m) => ({
    default: m.ModuleReview,
  }))
);
const ModuleServicer = lazy(() =>
  retryImport(() => import('../../misc/ModuleServicer')).then((m) => ({
    default: m.ModuleServicer,
  }))
);
const ModuleResultOptions = lazy(() =>
  retryImport(() => import('../../misc/ModuleResultOptions')).then((m) => ({
    default: m.ModuleResultOptions,
  }))
);

export const Module = observer(() => {
  const {
    decisionEngineStore,
    formStore,
    commonStore,
    partnerStore,
    initializeStore: { isLoading },
  } = useStore();
  const { getModuleElements, getSubmodule, currentModuleName, formData } =
    formStore;
  const { isBlacklisted } = decisionEngineStore;
  const moduleElements = getModuleElements;
  const subModules = getSubmodule;
  const {
    options: { introUI },
  } = partnerStore;

  const isMobile = useMediaQuery('(max-width:960px)');
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const yupSchema = useYupSchema(subModules || []);

  useEffect(() => {
    if (!shouldShowIntroUI(currentModuleName, introUI)) {
      formStore.next();
    }
    if (currentModuleName === 'MODULE_INTRO' && !isLoading) {
      decisionEngineStore.checkIfIpBlacklisted();
    }
  }, [currentModuleName, introUI, formStore, commonStore]);

  useEffect(() => {
    if (isMobile) {
      const mobileScrollDiv = document.getElementById(MOBILE_SCROLLING_DIV_ID);
      if (mobileScrollDiv) mobileScrollDiv.scrollTop = 0;
    } else {
      const mobileScrollDiv = document.getElementById(MOBILE_SCROLLING_DIV_ID);
      if (mobileScrollDiv) mobileScrollDiv.scrollTop = 0;

      const desktopScrollDiv = document.getElementById(
        DESKTOP_SCROLLING_DIV_ID
      );
      if (desktopScrollDiv) desktopScrollDiv.scrollTop = 0;
    }
  }, [isMobile, currentModuleName]);

  const methods = useForm({
    resolver: yupSchema ? yupResolver(yupSchema) : undefined,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: formData,
  });

  useEffect(() => {
    if (yupSchema && currentModuleName) {
      const dirtyFields = methods.formState.dirtyFields;
      const fields = Object.keys(dirtyFields);
      methods.trigger(fields);
    }
    return () => {
      formStore.setFormData(methods.getValues());
    };
  }, [methods, currentModuleName, yupSchema]);

  const onSubmit = (data: any) => {
    formStore.setFormData(data);
  };

  const isButtonDisabled =
    !yupSchema || !methods.formState.isValid || methods.formState.isValidating;

  const reportedReady = useRef(false);
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (
      reportedReady.current ||
      commonStore.loading ||
      commonStore.showServicer ||
      isLoading ||
      isBlacklisted ||
      !yupSchema ||
      !moduleElements ||
      ['MODULE_INTRO', 'MODULE_REVIEW', 'MODULE_RESULT_OPTIONS'].includes(
        currentModuleName
      )
    )
      return;
    // Wait for a paint of the mounted fields with their validation schema ready.
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        if (
          !formRef.current?.querySelector(
            'input:not([type="hidden"]), select, textarea'
          )
        )
          return;
        reportedReady.current = true;
        performance.mark('ida-form-ready');
        sendEventToHost('formReady');
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [
    commonStore.loading,
    commonStore.showServicer,
    isLoading,
    isBlacklisted,
    yupSchema,
    moduleElements,
    currentModuleName,
  ]);

  // First painted screen of any kind (intro, form, review, results, servicer): the
  // loader keeps the iframe invisible over the host page until this arrives.
  const reportedScreen = useRef(false);
  useEffect(() => {
    if (
      reportedScreen.current ||
      commonStore.loading ||
      isLoading ||
      !currentModuleName
    )
      return;
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        reportedScreen.current = true;
        sendEventToHost('screenReady');
        startAppCheck();
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      cancelAnimationFrame(secondFrame);
    };
  }, [commonStore.loading, isLoading, currentModuleName]);

  const footerDivHeight = document.getElementById(FOOTER_DIV_ID)?.offsetHeight;

  if (commonStore.loading) {
    return <LoadingPage />;
  }

  if (commonStore.showServicer) {
    return (
      <Suspense fallback={<LoadingPage />}>
        <ModuleServicer />
      </Suspense>
    );
  }

  if (currentModuleName === 'MODULE_INTRO') {
    return (
      <Suspense fallback={<LoadingPage />}>
        <ModuleIntro />
      </Suspense>
    );
  }

  if (currentModuleName === 'MODULE_REVIEW') {
    return (
      <FormProvider {...methods}>
        <Suspense fallback={<LoadingPage />}>
          {isDesktop ? (
            <ModuleReview />
          ) : (
            <Box sx={{ height: 1 }}>
              <ModuleReview />
            </Box>
          )}
        </Suspense>
        <ModalFooter />
      </FormProvider>
    );
  }

  if (currentModuleName === 'MODULE_RESULT_OPTIONS') {
    return (
      <FormProvider {...methods}>
        <Suspense fallback={<LoadingPage />}>
          <ModuleResultOptions />
        </Suspense>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...methods}>
      {!isBlacklisted && (
        <Box
          data-cy="moduleFormProviderBox"
          sx={{
            height: '100%',
            position: isMobile ? 'block' : 'relative',
          }}
        >
          <Box
            sx={{
              height: '100%',
              '@media(min-width: 960px) and @media(min-height:700px)': {
                pb: '6vh',
              },
              '@media(min-height: 600px) and (max-height:700px) and @media(min-width: 960px)':
                {
                  pb: '9vh',
                },
            }}
          >
            <Grid
              data-cy="moduleGridContainer"
              container={!isMobile}
              sx={{
                width: '100%',
                height: isMobile ? 'auto' : '100%',
                overflowX: 'hidden',
                overflowY: 'hidden',
              }}
            >
              <ModuleHeader />
              <Grid
                data-cy="moduleGridContainerscroling"
                item
                id={DESKTOP_SCROLLING_DIV_ID}
                md={8}
                xs={12}
                sx={{
                  maxHeight: isMobile ? 'auto' : '600px',
                  height: isDesktop
                    ? `calc(100% - (${
                        footerDivHeight ? `${footerDivHeight}px` : '68px'
                      }))`
                    : 'auto',
                  display: 'flex',
                  flexGrow: 1,
                  overflowY: isMobile ? 'unset' : 'auto',
                }}
              >
                <Box
                  data-cy="moduleBoxBeforeStack"
                  sx={{
                    display: { xs: 'flex', md: 'block' },
                    flexDirection: 'column',
                    width: { xs: '100%', md: '400px' },
                    height: { xs: 'auto', md: '0%' },
                    margin: '0 auto',
                    padding: { xs: '0px 24px', md: '24px' },
                  }}
                >
                  <form ref={formRef} onSubmit={methods.handleSubmit(onSubmit)}>
                    {formStore.currentIndex === 1 && <RequiredIndicator />}
                    {moduleElements}
                  </form>
                </Box>
              </Grid>
            </Grid>
            <Box>
              <ModalFooter
                onSubmit={methods.handleSubmit(onSubmit)}
                isDisabled={isButtonDisabled}
                hideBorder={isMobile}
              />
            </Box>
          </Box>
        </Box>
      )}
    </FormProvider>
  );
});
